import type { MapOverlay } from '../types';

// Corner math for plan map overlays (mapOverlays/{id}). An overlay is placed
// by three corners of its image; the fourth follows, so the image can move,
// scale, rotate and shear but never bend. Everything here is pure: the
// Leaflet side (rotatedImageOverlay.ts) projects lat/lng to layer pixels and
// hands those pixels to cssMatrixForCorners.

export type LatLng = { lat: number; lng: number };
export type Point = { x: number; y: number };
export type OverlayCorners = MapOverlay['corners'];
export type CornerKey = keyof OverlayCorners;

export interface LatLngBoundsLike {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** The corner opposite topLeft, in whatever plane the three points share. */
export function fourthCorner(topLeft: Point, topRight: Point, bottomLeft: Point): Point {
  return {
    x: topRight.x + bottomLeft.x - topLeft.x,
    y: topRight.y + bottomLeft.y - topLeft.y,
  };
}

/** Same as fourthCorner in lat/lng. Exact in Web Mercator only for small
 *  extents, which a town-scale plan map is. Use the pixel version to draw. */
export function bottomRightLatLng(corners: OverlayCorners): LatLng {
  const p = fourthCorner(
    { x: corners.topLeft.lng, y: corners.topLeft.lat },
    { x: corners.topRight.lng, y: corners.topRight.lat },
    { x: corners.bottomLeft.lng, y: corners.bottomLeft.lat }
  );
  return { lat: p.y, lng: p.x };
}

/**
 * CSS `matrix(a, b, c, d, e, f)` (with `transform-origin: 0 0`) that maps an
 * element of width x height pixels onto the parallelogram whose corners are
 * topLeft, topRight and bottomLeft, all in the same pixel space as the
 * element's offset parent. Local (u, v) lands at
 * (a*u + c*v + e, b*u + d*v + f), so (0,0) -> topLeft, (width,0) -> topRight
 * and (0,height) -> bottomLeft.
 */
export function cssMatrixForCorners(
  topLeft: Point,
  topRight: Point,
  bottomLeft: Point,
  width: number,
  height: number
): [number, number, number, number, number, number] {
  return [
    (topRight.x - topLeft.x) / width,
    (topRight.y - topLeft.y) / width,
    (bottomLeft.x - topLeft.x) / height,
    (bottomLeft.y - topLeft.y) / height,
    topLeft.x,
    topLeft.y,
  ];
}

/**
 * Upright, unrotated corners that fit an image of the given pixel size
 * inside `fraction` of the bounds, centered, keeping the image's aspect.
 * Longitude degrees are scaled by cos(latitude) so the image isn't
 * stretched east-west.
 */
export function fitCornersToBounds(
  bounds: LatLngBoundsLike,
  imageWidth: number,
  imageHeight: number,
  fraction = 0.7
): OverlayCorners {
  const midLat = (bounds.north + bounds.south) / 2;
  const midLng = (bounds.east + bounds.west) / 2;
  const k = Math.cos((midLat * Math.PI) / 180);
  // View size in "latitude-degree" units on both axes.
  const viewW = Math.abs(bounds.east - bounds.west) * k;
  const viewH = Math.abs(bounds.north - bounds.south);
  const aspect = imageWidth / imageHeight;
  let w = viewW * fraction;
  let h = w / aspect;
  if (h > viewH * fraction) {
    h = viewH * fraction;
    w = h * aspect;
  }
  const halfLng = w / 2 / k;
  const halfLat = h / 2;
  return {
    topLeft: { lat: midLat + halfLat, lng: midLng - halfLng },
    topRight: { lat: midLat + halfLat, lng: midLng + halfLng },
    bottomLeft: { lat: midLat - halfLat, lng: midLng - halfLng },
  };
}

/** Shift every corner by the same lat/lng delta (dragging the image body). */
export function moveCorners(corners: OverlayCorners, dLat: number, dLng: number): OverlayCorners {
  const shift = (c: LatLng): LatLng => ({ lat: c.lat + dLat, lng: c.lng + dLng });
  return {
    topLeft: shift(corners.topLeft),
    topRight: shift(corners.topRight),
    bottomLeft: shift(corners.bottomLeft),
  };
}

/** Replace one corner (dragging a handle). The others stay put, so this
 *  stretches, rotates or shears around them. */
export function setCorner(corners: OverlayCorners, key: CornerKey, to: LatLng): OverlayCorners {
  return { ...corners, [key]: { lat: to.lat, lng: to.lng } };
}

/** True when the three corners are finite, in range and not collinear (a
 *  collinear set has no area and the CSS matrix would be singular). */
export function cornersAreValid(corners: OverlayCorners): boolean {
  const pts = [corners.topLeft, corners.topRight, corners.bottomLeft];
  const inRange = pts.every(
    (p) =>
      Number.isFinite(p.lat) &&
      Number.isFinite(p.lng) &&
      Math.abs(p.lat) <= 90 &&
      Math.abs(p.lng) <= 180
  );
  if (!inRange) return false;
  const [a, b, c] = pts;
  const cross = (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
  return Math.abs(cross) > 1e-12;
}
